import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  Image,
  Text,
  Heading,
  VStack,
  useColorMode,
  Badge,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Flex,
  Button,
  ButtonGroup,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  Select,
  Menu as ChakraMenu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  useDisclosure,
} from "@chakra-ui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  SearchIcon,
  ChevronDownIcon,
  CloseIcon,
} from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { Book } from "../libs/types";
import { APP_CONSTANTS } from "../constants";
import { smoothScrollTo } from "../utils/scrollUtils";
import { getBooks } from "../libs/qazbilim-backend";
import { LIBRARY_BOOKS_KEY } from "../libs/book-swr";
import { useBookStore } from "../store/bookStore";
import Menu from "./Menu";
import BookCard from "./BookCard";
import LoadingLogo from "./LoadingLogo";

const BOOKS_PER_PAGE = 6;

// Enhanced fuzzy search function that scores matches based on similarity
const fuzzyMatch = (query: string, target: string): boolean => {
  if (!target) return false;
  if (!query) return true;

  query = query.toLowerCase().trim();
  target = target.toLowerCase();

  // Exact match or contained match (highest priority)
  if (target.includes(query)) return true;

  // Split query into words for multi-word search
  const words = query.split(/\s+/);

  // If all words are found in the target (in any order), consider it a match
  if (words.length > 1) {
    return words.every((word) => target.includes(word));
  }

  // Character by character fuzzy matching for single words
  let i = 0,
    j = 0;
  while (i < query.length && j < target.length) {
    if (query[i] === target[j]) {
      i++;
    }
    j++;
  }

  // Match if we've gone through all characters in the query
  return i === query.length;
};

const Library: React.FC = () => {
  const { colorMode } = useColorMode();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    data: books = [],
    error: booksError,
    isLoading,
  } = useSWR(LIBRARY_BOOKS_KEY, () => getBooks());
  const error = booksError
    ? booksError instanceof Error
      ? booksError.message
      : String(booksError)
    : null;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchFilter, setSearchFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("none");
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const modalSearchInputRef = useRef<HTMLInputElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Handle keyboard shortcut '/' to open search modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Open search modal when '/' is pressed, unless user is typing in another input
      if (
        event.key === "/" &&
        document.activeElement?.tagName !== "INPUT" &&
        document.activeElement?.tagName !== "TEXTAREA"
      ) {
        event.preventDefault();
        onOpen();
      }

      // Clear search with Escape key when search input is focused
      if (
        event.key === "Escape" &&
        (document.activeElement === searchInputRef.current ||
          document.activeElement === modalSearchInputRef.current) &&
        searchQuery
      ) {
        setSearchQuery("");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [searchQuery, onOpen]);

  // Focus the modal search input when the modal opens
  useEffect(() => {
    if (isOpen && modalSearchInputRef.current) {
      setTimeout(() => {
        modalSearchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Filter books based on search query
  const filteredBooks = books.filter((book) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.trim().toLowerCase();

    switch (searchFilter) {
      case "title":
        return fuzzyMatch(query, book.title);
      case "author":
        return fuzzyMatch(query, book.author);
      case "type":
        return fuzzyMatch(query, book.type || "");
      case "all":
      default:
        return (
          fuzzyMatch(query, book.title) ||
          fuzzyMatch(query, book.author) ||
          fuzzyMatch(query, book.type || "")
        );
    }
  });

  // Sort books based on sort option
  const sortedBooks = [...filteredBooks].sort((a, b) => {
    switch (sortBy) {
      case "title":
        return a.title.localeCompare(b.title);
      case "title-desc":
        return b.title.localeCompare(a.title);
      case "author":
        return a.author.localeCompare(b.author);
      case "author-desc":
        return b.author.localeCompare(a.author);
      default:
        return 0; // No sorting
    }
  });

  // Calculate pagination values
  const totalBooks = sortedBooks.length;
  const totalPages = Math.ceil(totalBooks / BOOKS_PER_PAGE);
  const startIndex = (currentPage - 1) * BOOKS_PER_PAGE;
  const endIndex = startIndex + BOOKS_PER_PAGE;
  const currentBooks = sortedBooks.slice(startIndex, endIndex);

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, searchFilter, sortBy]);

  // Pagination handlers
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      smoothScrollTo(0);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      smoothScrollTo(0);
    }
  };

  const goToPage = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      smoothScrollTo(0);
    }
  };

  // Handle book click to navigate to read view
  const handleBookClick = (book: Book) => {
    // Reset the book store to ensure a clean state
    useBookStore.getState().resetState();
    navigate(`/read/${book.id}/1`);
  };

  // Get sort option label for display
  const getSortLabel = () => {
    switch (sortBy) {
      case "title":
        return t("library.sort.titleAsc");
      case "title-desc":
        return t("library.sort.titleDesc");
      case "author":
        return t("library.sort.authorAsc");
      case "author-desc":
        return t("library.sort.authorDesc");
      default:
        return t("library.sort.none");
    }
  };

  // Get filter option label for display
  const getFilterLabel = () => {
    switch (searchFilter) {
      case "title":
        return t("library.searchFilter.title");
      case "author":
        return t("library.searchFilter.author");
      case "type":
        return t("library.searchFilter.type");
      case "all":
      default:
        return t("library.searchFilter.all");
    }
  };

  if (isLoading) {
    return (
      <Center minH="400px">
        <VStack spacing={4}>
          <LoadingLogo size="xl" />
          <Text>{t("library.loading")}</Text>
        </VStack>
      </Center>
    );
  }

  // Pagination UI component
  const PaginationControls = () => {
    if (totalPages <= 1) return null;

    // Generate page numbers with an improved display for many pages
    const getPageNumbers = () => {
      const pageNumbers = [];

      // For small number of pages, show all
      if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
        return pageNumbers;
      }

      // Always show first page
      pageNumbers.push(1);

      // Calculate range around current page
      const leftBound = Math.max(2, currentPage - 1);
      const rightBound = Math.min(totalPages - 1, currentPage + 1);

      // Add ellipsis if needed before current page range
      if (leftBound > 2) {
        pageNumbers.push("ellipsis-left");
      }

      // Add pages around current page
      for (let i = leftBound; i <= rightBound; i++) {
        pageNumbers.push(i);
      }

      // Add ellipsis if needed after current page range
      if (rightBound < totalPages - 1) {
        pageNumbers.push("ellipsis-right");
      }

      // Always show last page
      pageNumbers.push(totalPages);

      return pageNumbers;
    };

    return (
      <Flex justify="center" mt={8} mb={4}>
        <ButtonGroup
          variant="outline"
          isAttached
          bg="transparent"
          spacing="1px"
        >
          <Button
            leftIcon={<ChevronLeftIcon />}
            onClick={goToPreviousPage}
            isDisabled={currentPage === 1}
            color={colorMode === "light" ? undefined : "white"}
            _hover={
              colorMode === "light"
                ? { bg: "gray.100" }
                : { bg: "rgba(255,255,255,0.08)" }
            }
            borderColor="transparent"
          >
            <Box display={{ base: "none", md: "inline" }}>
              {t("library.pagination.previous")}
            </Box>
          </Button>

          {/* Page number buttons */}
          {getPageNumbers().map((page, index) => {
            if (page === "ellipsis-left" || page === "ellipsis-right") {
              return (
                <Button
                  key={page}
                  variant="outline"
                  isDisabled
                  cursor="default"
                  w={{ base: "30px", md: "40px" }}
                  p={0}
                  color={colorMode === "light" ? undefined : "white"}
                  borderColor="transparent"
                >
                  ...
                </Button>
              );
            }

            return (
              <Button
                key={index}
                onClick={() => goToPage(Number(page))}
                variant={currentPage === page ? "solid" : "outline"}
                color={
                  colorMode === "light"
                    ? currentPage === page
                      ? "white"
                      : undefined
                    : currentPage === page
                      ? "white"
                      : "gray.200"
                }
                bg={
                  currentPage === page
                    ? colorMode === "light"
                      ? "blue.600"
                      : "whiteAlpha.200"
                    : "transparent"
                }
                _hover={
                  currentPage === page
                    ? colorMode === "light"
                      ? { bg: "blue.700" }
                      : { bg: "whiteAlpha.300" }
                    : colorMode === "light"
                      ? { bg: "gray.100" }
                      : { bg: "rgba(255,255,255,0.08)" }
                }
                _active={
                  currentPage === page
                    ? undefined
                    : colorMode === "light"
                      ? { bg: "blue.50", color: "blue.700" }
                      : { bg: "whiteAlpha.200", color: "white" }
                }
                borderColor={
                  currentPage === page
                    ? colorMode === "light"
                      ? "blue.600"
                      : "whiteAlpha.200"
                    : "transparent"
                }
                mx="1px"
                minW={{ base: "30px", md: "40px" }}
              >
                {page}
              </Button>
            );
          })}

          <Button
            rightIcon={<ChevronRightIcon />}
            onClick={goToNextPage}
            isDisabled={currentPage === totalPages}
            color={colorMode === "light" ? undefined : "white"}
            _hover={
              colorMode === "light"
                ? { bg: "gray.100" }
                : { bg: "rgba(255,255,255,0.08)" }
            }
            borderColor="transparent"
          >
            <Box display={{ base: "none", md: "inline" }}>
              {t("library.pagination.next")}
            </Box>
          </Button>
        </ButtonGroup>
      </Flex>
    );
  };

  // Search Modal Component
  const SearchModal = () => {
    return (
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(5px)" />
        <ModalContent
          bg={colorMode === "light" ? "white" : "gray.800"}
          boxShadow="xl"
          borderRadius="lg"
          mx={{ base: 3, md: 0 }}
        >
          <ModalBody p={{ base: 4, md: 5 }}>
            <InputGroup size="lg">
              <InputLeftElement pointerEvents="none">
                <SearchIcon
                  color={colorMode === "light" ? "gray.400" : "gray.300"}
                  boxSize={5}
                />
              </InputLeftElement>
              <Input
                ref={modalSearchInputRef}
                placeholder={t("library.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg={colorMode === "light" ? "white" : "gray.700"}
                color={colorMode === "light" ? "black" : "white"}
                borderColor={colorMode === "light" ? "gray.200" : "gray.600"}
                borderRadius="md"
                size="lg"
                _placeholder={{
                  color: colorMode === "light" ? "gray.400" : "gray.300",
                }}
                _focus={{
                  borderColor: colorMode === "light" ? "blue.500" : "blue.300",
                  boxShadow:
                    colorMode === "light"
                      ? "0 0 0 1px rgba(66, 153, 225, 0.6)"
                      : "0 0 0 1px rgba(99, 179, 237, 0.6)",
                }}
                autoFocus
              />
              {searchQuery && (
                <Button
                  position="absolute"
                  right="1rem"
                  top="50%"
                  transform="translateY(-50%)"
                  size="sm"
                  variant="ghost"
                  colorScheme="gray"
                  onClick={() => setSearchQuery("")}
                  zIndex={2}
                  color={colorMode === "light" ? undefined : "gray.300"}
                  _hover={{
                    bg:
                      colorMode === "light"
                        ? undefined
                        : "rgba(255,255,255,0.08)",
                  }}
                  aria-label={t("library.clearSearch")}
                  borderRadius="full"
                  p={1}
                >
                  <CloseIcon boxSize={3} />
                </Button>
              )}
            </InputGroup>
            <Flex
              mt={{ base: 3, md: 4 }}
              width="100%"
              direction={{ base: "column", md: "row" }}
              gap={{ base: 2, md: 4 }}
            >
              {/* Search Filter - full width on small screens, 50% on medium+ */}
              <Box width={{ base: "100%", md: "50%" }} minWidth={0}>
                <ChakraMenu>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    bg={colorMode === "light" ? "white" : "gray.700"}
                    color={colorMode === "light" ? "black" : "white"}
                    borderColor={
                      colorMode === "light" ? "gray.200" : "gray.600"
                    }
                    _hover={{
                      bg: colorMode === "light" ? "gray.100" : "gray.600",
                    }}
                    _active={{
                      bg: colorMode === "light" ? "gray.200" : "gray.500",
                    }}
                    aria-label={t("library.searchFilter")}
                    width="100%"
                  >
                    {getFilterLabel()}
                  </MenuButton>
                  <MenuList>
                    <MenuItem onClick={() => setSearchFilter("all")}>
                      {t("library.searchFilter.all")}
                    </MenuItem>
                    <MenuItem onClick={() => setSearchFilter("title")}>
                      {t("library.searchFilter.title")}
                    </MenuItem>
                    <MenuItem onClick={() => setSearchFilter("author")}>
                      {t("library.searchFilter.author")}
                    </MenuItem>
                    <MenuItem onClick={() => setSearchFilter("type")}>
                      {t("library.searchFilter.type")}
                    </MenuItem>
                  </MenuList>
                </ChakraMenu>
              </Box>

              {/* Sort Button - full width on small screens, 50% on medium+ */}
              <Box width={{ base: "100%", md: "50%" }} minWidth={0}>
                <ChakraMenu>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    bg={colorMode === "light" ? "white" : "gray.700"}
                    color={colorMode === "light" ? "black" : "white"}
                    borderColor={
                      colorMode === "light" ? "gray.200" : "gray.600"
                    }
                    _hover={{
                      bg: colorMode === "light" ? "gray.100" : "gray.600",
                    }}
                    _active={{
                      bg: colorMode === "light" ? "gray.200" : "gray.500",
                    }}
                    width="100%"
                  >
                    {getSortLabel()}
                  </MenuButton>
                  <MenuList>
                    <MenuItem onClick={() => setSortBy("none")}>
                      {t("library.sort.none")}
                    </MenuItem>
                    <MenuItem onClick={() => setSortBy("title")}>
                      {t("library.sort.titleAsc")}
                    </MenuItem>
                    <MenuItem onClick={() => setSortBy("title-desc")}>
                      {t("library.sort.titleDesc")}
                    </MenuItem>
                    <MenuItem onClick={() => setSortBy("author")}>
                      {t("library.sort.authorAsc")}
                    </MenuItem>
                    <MenuItem onClick={() => setSortBy("author-desc")}>
                      {t("library.sort.authorDesc")}
                    </MenuItem>
                  </MenuList>
                </ChakraMenu>
              </Box>
            </Flex>
            {searchQuery.trim() && (
              <Text
                mt={{ base: 2, md: 3 }}
                color={colorMode === "light" ? "gray.600" : "gray.300"}
                fontSize="sm"
              >
                {t("library.searchResults", {
                  count: filteredBooks.length,
                  query: searchQuery,
                })}
              </Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  };

  return (
    <Box p={{ base: 0, md: 6 }} maxW="container.md" mx="auto">
      <Box px={{ base: 1, md: 0 }}>
        <Heading
          size={{ base: "md", md: "lg" }}
          mb={{ base: 4, md: 6 }}
          color={colorMode === "light" ? "gray.800" : "white"}
        >
          {t("library.title")}
        </Heading>

        {error && (
          <Alert
            status="warning"
            mb={{ base: 3, md: 6 }}
            borderRadius="md"
            bg={colorMode === "light" ? undefined : "rgba(45, 55, 72, 0.8)"}
            color={colorMode === "light" ? undefined : "white"}
          >
            <AlertIcon />
            {t("error.bookLoadFail")}
          </Alert>
        )}

        {/* Search Modal */}
        <SearchModal />

        {/* Search Input */}
        <Box
          mb={{ base: 3, md: 6 }}
          p={{ base: 3, md: 4 }}
          bg={
            colorMode === "light"
              ? "rgba(240, 240, 240, 0.6)"
              : "rgba(30, 35, 45, 0.8)"
          }
          borderRadius="md"
          boxShadow={colorMode === "light" ? "sm" : "md"}
          width="100%"
          minWidth={0}
        >
          <VStack spacing={{ base: 2, md: 4 }} width="100%">
            <InputGroup size="md" width="100%">
              <InputLeftElement pointerEvents="none">
                <SearchIcon
                  color={colorMode === "light" ? "gray.400" : "gray.300"}
                />
              </InputLeftElement>
              <Input
                ref={searchInputRef}
                placeholder={t("library.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg={colorMode === "light" ? "white" : "gray.700"}
                color={colorMode === "light" ? "black" : "white"}
                borderColor={colorMode === "light" ? "gray.200" : "gray.600"}
                borderRadius="md"
                _placeholder={{
                  color: colorMode === "light" ? "gray.400" : "gray.300",
                }}
                _focus={{
                  borderColor: colorMode === "light" ? "blue.500" : "blue.300",
                  boxShadow:
                    colorMode === "light"
                      ? "0 0 0 1px rgba(66, 153, 225, 0.6)"
                      : "0 0 0 1px rgba(99, 179, 237, 0.6)",
                }}
                _hover={{
                  borderColor: colorMode === "light" ? "gray.300" : "gray.500",
                }}
                transition="all 0.2s"
                aria-label={t("library.searchPlaceholder")}
                width="100%"
                minWidth={0}
              />
              {searchQuery && (
                <Button
                  position="absolute"
                  right="1rem"
                  top="50%"
                  transform="translateY(-50%)"
                  size="sm"
                  variant="ghost"
                  colorScheme="gray"
                  onClick={() => setSearchQuery("")}
                  zIndex={2}
                  color={colorMode === "light" ? undefined : "gray.300"}
                  _hover={{
                    bg:
                      colorMode === "light"
                        ? undefined
                        : "rgba(255,255,255,0.08)",
                  }}
                  aria-label={t("library.clearSearch")}
                  borderRadius="full"
                  p={1}
                >
                  <CloseIcon boxSize={3} />
                </Button>
              )}
            </InputGroup>
            <Flex
              width="100%"
              direction={{ base: "column", md: "row" }}
              gap={{ base: 2, md: 4 }}
            >
              {/* Search Filter - full width on small screens, 50% on medium+ */}
              <Box width={{ base: "100%", md: "50%" }} minWidth={0}>
                <ChakraMenu>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    bg={colorMode === "light" ? "white" : "gray.700"}
                    color={colorMode === "light" ? "black" : "white"}
                    borderColor={
                      colorMode === "light" ? "gray.200" : "gray.600"
                    }
                    _hover={{
                      bg: colorMode === "light" ? "gray.100" : "gray.600",
                    }}
                    _active={{
                      bg: colorMode === "light" ? "gray.200" : "gray.500",
                    }}
                    aria-label={t("library.searchFilter")}
                    width="100%"
                  >
                    {getFilterLabel()}
                  </MenuButton>
                  <MenuList>
                    <MenuItem onClick={() => setSearchFilter("all")}>
                      {t("library.searchFilter.all")}
                    </MenuItem>
                    <MenuItem onClick={() => setSearchFilter("title")}>
                      {t("library.searchFilter.title")}
                    </MenuItem>
                    <MenuItem onClick={() => setSearchFilter("author")}>
                      {t("library.searchFilter.author")}
                    </MenuItem>
                    <MenuItem onClick={() => setSearchFilter("type")}>
                      {t("library.searchFilter.type")}
                    </MenuItem>
                  </MenuList>
                </ChakraMenu>
              </Box>

              {/* Sort Button - full width on small screens, 50% on medium+ */}
              <Box width={{ base: "100%", md: "50%" }} minWidth={0}>
                <ChakraMenu>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    bg={colorMode === "light" ? "white" : "gray.700"}
                    color={colorMode === "light" ? "black" : "white"}
                    borderColor={
                      colorMode === "light" ? "gray.200" : "gray.600"
                    }
                    _hover={{
                      bg: colorMode === "light" ? "gray.100" : "gray.600",
                    }}
                    _active={{
                      bg: colorMode === "light" ? "gray.200" : "gray.500",
                    }}
                    width="100%"
                  >
                    {getSortLabel()}
                  </MenuButton>
                  <MenuList>
                    <MenuItem onClick={() => setSortBy("none")}>
                      {t("library.sort.none")}
                    </MenuItem>
                    <MenuItem onClick={() => setSortBy("title")}>
                      {t("library.sort.titleAsc")}
                    </MenuItem>
                    <MenuItem onClick={() => setSortBy("title-desc")}>
                      {t("library.sort.titleDesc")}
                    </MenuItem>
                    <MenuItem onClick={() => setSortBy("author")}>
                      {t("library.sort.authorAsc")}
                    </MenuItem>
                    <MenuItem onClick={() => setSortBy("author-desc")}>
                      {t("library.sort.authorDesc")}
                    </MenuItem>
                  </MenuList>
                </ChakraMenu>
              </Box>
            </Flex>
          </VStack>
        </Box>

        {/* Search results count when filtering */}
        {searchQuery.trim() && (
          <Flex mb={{ base: 2, md: 4 }} justify="space-between" align="center">
            <Text
              color={colorMode === "light" ? "gray.600" : "gray.300"}
              fontSize="sm"
            >
              {t("library.searchResults", {
                count: filteredBooks.length,
                query: searchQuery,
              })}
            </Text>
            {filteredBooks.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSearchQuery("")}
                aria-label={t("library.clearSearch")}
                borderRadius="full"
                p={1}
              >
                <CloseIcon boxSize={3} />
              </Button>
            )}
          </Flex>
        )}
      </Box>

      {filteredBooks.length === 0 ? (
        <Box px={{ base: 1, md: 0 }}>
          <Center
            minH={{ base: "150px", md: "200px" }}
            bg={
              colorMode === "light"
                ? "rgba(240, 240, 240, 0.6)"
                : "rgba(30, 35, 45, 0.8)"
            }
            borderRadius="md"
            p={{ base: 3, md: 4 }}
          >
            <Text fontSize="lg">
              {books.length === 0
                ? t("library.emptyLibrary")
                : t("library.noResults")}
            </Text>
          </Center>
        </Box>
      ) : (
        <>
          <Grid
            templateColumns={[
              "repeat(1, 1fr)",
              "repeat(2, 1fr)",
              "repeat(3, 1fr)",
            ]}
            gap={{ base: 3, md: 6 }}
            px={{ base: 1, md: 0 }}
          >
            {currentBooks.map((book) => (
              <Box
                key={book.id}
                borderRadius="md"
                overflow="hidden"
                bg={
                  colorMode === "light"
                    ? "rgba(240, 240, 240, 0.6)"
                    : "rgba(30, 35, 45, 0.8)"
                }
                boxShadow="sm"
                cursor="pointer"
                transition="transform 0.2s, box-shadow 0.2s, background-color 0.2s"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "lg",
                  bg:
                    colorMode === "light"
                      ? "rgba(248, 248, 248, 0.9)"
                      : "rgba(38, 46, 63, 0.9)",
                }}
                _active={{
                  transform: "translateY(-2px)",
                  boxShadow: "md",
                }}
                onClick={() => handleBookClick(book)}
                width={{ base: "80%", md: "auto" }}
                mx={{ base: "auto", md: "0" }}
              >
                <Box
                  position="relative"
                  width="100%"
                  mx="auto"
                  height="220px"
                  mb={2}
                  onClick={() => handleBookClick(book)}
                  cursor="pointer"
                >
                  <BookCard
                    title={book.title}
                    coverUrl={book.coverUrl}
                    width="100%"
                    height="220px"
                    boxShadow="md"
                    borderRadius="md"
                  />
                </Box>
                <VStack p={3} align="start" spacing={1}>
                  <Heading size="md" noOfLines={1}>
                    {book.title}
                  </Heading>
                  <Text
                    color={colorMode === "light" ? "gray.500" : "gray.400"}
                    fontSize="sm"
                  >
                    {book.author}
                  </Text>
                </VStack>
              </Box>
            ))}
          </Grid>

          <Box px={{ base: 1, md: 0 }}>
            <PaginationControls />
          </Box>
        </>
      )}
    </Box>
  );
};

export default Library;
